import { useEffect, useId, useRef, useState } from 'react'
import { NavLink, Outlet, useLocation } from 'react-router-dom'
import { useAuth } from '../auth/AuthContext'
import { NotificationBell } from './NotificationBell'
import { UserMenu } from './UserMenu'
import { ZaloFab } from './ZaloFab'

type NavItem = {
  to: string
  label: string
  icon: string
  external?: boolean
  end?: boolean
}

type NavGroup = {
  id: string
  label: string
  icon: string
  items: NavItem[]
}

const GAME_ITEMS: NavItem[] = [
  { to: '/mua-minecraft', label: 'Mua key Minecraft', icon: 'fa-key' },
  { to: '/buyitforme', label: 'Đặt hộ', icon: 'fa-handshake' },
  { to: '/cape', label: 'Cape / OptiFine', icon: 'fa-shirt' },
  { to: '/minecoin', label: 'Minecoin', icon: 'fa-coins' },
  { to: '/digital-shop', label: 'Phần mềm', icon: 'fa-laptop-code' },
]

const RESOURCE_ITEMS: NavItem[] = [
  { to: '/tai-nguyen', label: 'Resource Pack', icon: 'fa-images' },
  { to: '/shader-pack', label: 'Shader Pack', icon: 'fa-sun' },
  { to: '/tai-game', label: 'Tải game Minecraft', icon: 'fa-gamepad' },
  { to: '/blog', label: 'Bài viết', icon: 'fa-newspaper' },
  { to: '/faq', label: 'FAQs', icon: 'fa-circle-question' },
  { to: '/may-chu-minecraft', label: 'Server list', icon: 'fa-server' },
]

const GROUPS: NavGroup[] = [
  {
    id: 'game',
    label: 'Trò chơi Minecraft',
    icon: 'fa-cube',
    items: GAME_ITEMS,
  },
  {
    id: 'resources',
    label: 'Tài nguyên',
    icon: 'fa-folder-open',
    items: RESOURCE_ITEMS,
  },
]

function pathMatches(pathname: string, to: string, end?: boolean) {
  if (to.startsWith('http')) return false
  if (end) return pathname === to
  return pathname === to || pathname.startsWith(`${to}/`)
}

function groupIsActive(pathname: string, items: NavItem[]) {
  return items.some((item) => !item.external && pathMatches(pathname, item.to, item.end))
}

function NavDropdown({ group, open, onToggle }: {
  group: NavGroup
  open: boolean
  onToggle: () => void
}) {
  const location = useLocation()
  const menuId = useId()
  const active = groupIsActive(location.pathname, group.items)

  return (
    <div className={`nav-dd${open ? ' open' : ''}${active ? ' active' : ''}`}>
      <button
        type="button"
        className="nav-dd-trigger"
        aria-expanded={open}
        aria-haspopup="menu"
        aria-controls={menuId}
        onClick={onToggle}
      >
        <i className={`fa-solid ${group.icon}`} aria-hidden />
        <span>{group.label}</span>
        <i className="fa-solid fa-chevron-down nav-dd-caret" aria-hidden />
      </button>
      <div className="nav-dd-menu" id={menuId} role="menu" hidden={!open}>
        {group.items.map((item) =>
          item.external ? (
            <a
              key={item.to}
              className="nav-dd-item"
              href={item.to}
              target="_blank"
              rel="noreferrer"
              role="menuitem"
            >
              <i className={`fa-solid ${item.icon}`} aria-hidden />
              {item.label}
            </a>
          ) : (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) => `nav-dd-item${isActive ? ' active' : ''}`}
              role="menuitem"
            >
              <i className={`fa-solid ${item.icon}`} aria-hidden />
              {item.label}
            </NavLink>
          ),
        )}
      </div>
    </div>
  )
}

export function Layout() {
  const { user, logout } = useAuth()
  const location = useLocation()
  const [openId, setOpenId] = useState<string | null>(null)
  const navRef = useRef<HTMLElement>(null)

  useEffect(() => {
    setOpenId(null)
  }, [location.pathname])

  useEffect(() => {
    function onDoc(e: MouseEvent) {
      if (!navRef.current?.contains(e.target as Node)) setOpenId(null)
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpenId(null)
    }
    document.addEventListener('mousedown', onDoc)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDoc)
      document.removeEventListener('keydown', onKey)
    }
  }, [])

  return (
    <div className="shell">
      <div className="demo-banner">
        <i className="fa-solid fa-flask" aria-hidden /> UI + API demo · data live từ Accminecraft ·{' '}
        <code>demo.accminecraft.net</code>
      </div>
      <header className="nav">
        <div className="container nav-inner">
          <NavLink to="/" className="brand">
            <img
              className="brand-logo"
              src="/image/logo2.png"
              alt="Accminecraft"
              width={40}
              height={40}
            />
            <span className="brand-text">Accminecraft</span>
          </NavLink>
          <nav className="nav-links" aria-label="Chính" ref={navRef}>
            <NavLink
              to="/"
              end
              className={({ isActive }) => `nav-link-plain${isActive ? ' active' : ''}`}
            >
              <i className="fa-solid fa-house" aria-hidden />
              Trang chủ
            </NavLink>
            {GROUPS.map((group) => (
              <NavDropdown
                key={group.id}
                group={group}
                open={openId === group.id}
                onToggle={() => setOpenId((cur) => (cur === group.id ? null : group.id))}
              />
            ))}
            <NavLink
              to="/reviews"
              className={({ isActive }) => `nav-link-plain${isActive ? ' active' : ''}`}
            >
              <i className="fa-solid fa-star" aria-hidden />
              Đánh giá
            </NavLink>
          </nav>
          <div className="nav-actions">
            {user ? (
              <>
                <NavLink to="/deposit" className="nav-wallet" title="Nạp tiền">
                  <i className="fa-regular fa-gem" aria-hidden />
                  <span>{user.balance2.toLocaleString('vi-VN')}</span>
                  <i className="fa-solid fa-plus nav-wallet-add" aria-hidden />
                </NavLink>
                <NotificationBell />
                <UserMenu user={user} onLogout={logout} />
              </>
            ) : (
              <>
                <NavLink to="/login" className="nav-ghost">
                  <i className="fa-solid fa-right-to-bracket" aria-hidden /> Đăng nhập
                </NavLink>
                <NavLink to="/mua-minecraft" className="nav-cta">
                  <i className="fa-solid fa-cart-plus" aria-hidden /> Mua key
                </NavLink>
              </>
            )}
          </div>
        </div>
      </header>
      <main className="main">
        <div className="container">
          <Outlet />
        </div>
      </main>
      <footer className="footer">
        <div className="container footer-grid">
          <div>
            <strong>
              <img
                src="/image/logo2.png"
                alt=""
                width={28}
                height={28}
                style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: 8 }}
              />
              Accminecraft
            </strong>
            <p style={{ margin: '0.4rem 0 0' }}>
              Demo refactor — catalog/auth/order nối API Node 24.
            </p>
          </div>
          <div>
            <strong>
              <i className="fa-solid fa-cube" aria-hidden /> Trò chơi
            </strong>
            <p style={{ margin: '0.4rem 0 0' }}>
              <NavLink to="/mua-minecraft">Key</NavLink> ·{' '}
              <NavLink to="/buyitforme">Mua hộ</NavLink> ·{' '}
              <NavLink to="/cape">Cape</NavLink>
            </p>
          </div>
          <div>
            <strong>
              <i className="fa-solid fa-folder-open" aria-hidden /> Tài nguyên
            </strong>
            <p style={{ margin: '0.4rem 0 0' }}>
              <NavLink to="/tai-nguyen">Packs</NavLink> · <NavLink to="/tai-game">Tải game</NavLink> ·{' '}
              <NavLink to="/blog">Blog</NavLink> · <NavLink to="/faq">FAQs</NavLink> ·{' '}
              <NavLink to="/may-chu-minecraft">Server list</NavLink> · <NavLink to="/reviews">Đánh giá</NavLink>
            </p>
          </div>
        </div>
      </footer>
      <ZaloFab />
    </div>
  )
}
